import { BroadcastState } from "./types";
import { ActionTree } from "vuex";
import { RootState } from "@/store/types";
import { graph } from "@/store/api";
import { gql } from "@apollo/client/core";
import _ from "lodash";

/**
 * Actions for the Broadcast module.
 * Handles async operations for broadcast messaging and audience targeting.
 */
const actions: ActionTree<BroadcastState, RootState> = {
  /**
   * Fetches all broadcasts from the API and commits to state.
   *
   * @param {ActionContext} context - Vuex action context
   * @returns {Promise<Object>} GraphQL response
   */
  FETCH_BROADCASTS: ({ commit }) => {
    return graph
      .query({
        query: gql`
          query {
            Bot {
              broadcasts
            }
          }
        `,
        fetchPolicy: "network-only",
      })
      .then((response) => {
        const broadcastArray = _.get(response, "data.Bot");
        // console.log("broadcastArray", broadcastArray);
        commit("SET_BROADCASTS", broadcastArray);
        return response;
      })
      .catch((err) => {
        throw err;
      });
  },

  /**
   * Updates an existing broadcast, removing old entry and adding updated one.
   *
   * @param {ActionContext} context - Vuex action context
   * @param {Object} payload - Action payload
   * @param {string} payload.oldTimestamp - Previous timestamp to remove
   * @param {IBroadcast} payload.broadcast - Updated broadcast object
   * @returns {Promise<boolean>} True if updated successfully
   */
  UPDATE_BROADCAST: (store, { oldTimestamp, broadcast }) => {
    return graph
      .mutate({
        mutation: gql`
          mutation($broadcast: JSON) {
            updateBroadcast(broadcast: $broadcast)
          }
        `,
        variables: {
          broadcast,
        },
      })
      .then((result) => {
        const isUpdated = _.get(result, "data.updateBroadcast");
        store.commit("UNSET_BROADCAST", oldTimestamp);
        store.commit("SET_BROADCAST", {
          broadcast: broadcast,
        });

        return isUpdated;
      })
      .catch((err) => {
        throw err;
      });
  },

  /**
   * Deletes a broadcast from API and state.
   *
   * @param {ActionContext} context - Vuex action context
   * @param {Object} payload - Action payload
   * @param {string} payload.timestamp - Timestamp of broadcast to delete
   * @returns {Promise<Object>} Deletion result
   */
  DELETE_BROADCAST: (store, { timestamp }) => {
    return graph
      .mutate({
        mutation: gql`
          mutation($timestamp: String) {
            deleteBroadcast(timestamp: $timestamp)
          }
        `,
        variables: {
          timestamp,
        },
      })
      .then((data) => {
        store.commit("DELETE_BROADCAST", timestamp);
        return data;
      })
      .catch((err) => {
        throw err;
      });
  },

  /**
   * Creates a new broadcast and selects it.
   *
   * @param {ActionContext} context - Vuex action context
   * @param {Object} payload - Action payload
   * @param {string} payload.timestamp - Timestamp for the broadcast
   * @param {IBroadcast} payload.broadcast - Broadcast object to create
   * @returns {Promise<IBroadcast>} Created broadcast
   */
  CREATE_BROADCAST: (store, { timestamp, broadcast }) => {
    graph
      .mutate({
        mutation: gql`
          mutation($timestamp: String, $broadcast: JSON) {
            setBroadcast(timestamp: $timestamp, broadcast: $broadcast)
          }
        `,
        variables: {
          timestamp,
          broadcast,
        },
      })
      .then((result) => {
        const broadcast = _.get(result, "data.setBroadcast");
        store.commit("CREATE_BROADCAST", {
          broadcast,
          timestamp,
        });
        store.commit("SET_SELECTED_BROADCAST", timestamp);
        return broadcast;
      })
      .catch((err) => {
        throw err;
      });
  },

  /**
   * Edits a broadcast by deleting the old one (if timestamp changed) and creating a new one.
   *
   * @param {ActionContext} context - Vuex action context
   * @param {Object} payload - Action payload
   * @param {string} payload.previous - Previous timestamp
   * @param {string} payload.timestamp - New timestamp
   * @param {IBroadcast} payload.broadcast - Updated broadcast object
   * @returns {Promise<IBroadcast>} Updated broadcast
   */
  EDIT_BROADCAST: (store, { previous, timestamp, broadcast }) => {
    if (previous !== timestamp) {
      store
        .dispatch("DELETE_BROADCAST", {
          timestamp: previous,
        })
        .catch((err) => {
          throw err;
        });
    }

    return store
      .dispatch("CREATE_BROADCAST", {
        timestamp,
        broadcast,
      })
      .catch((err) => {
        throw err;
      });
  },

  /**
   * Tests a broadcast by sending it to a specific test user.
   *
   * @param {ActionContext} context - Vuex action context
   * @param {Object} payload - Action payload
   * @param {string} payload.timestamp - Broadcast timestamp
   * @param {IBroadcast} payload.broadcast - Broadcast object to test
   * @param {string} payload.testUserId - Facebook user ID to send test to
   * @returns {Promise<boolean>} True if test successful
   */
  TEST_BROADCAST: (store, { timestamp, broadcast, testUserId }) => {
    return graph
      .mutate({
        mutation: gql`
          mutation($timestamp: String, $testUserId: String, $broadcast: JSON) {
            testBroadcast(timestamp: $timestamp, testUserId: $testUserId, broadcast: $broadcast)
          }
        `,
        variables: {
          timestamp,
          broadcast,
          testUserId,
        },
      })
      .then((result) => {
        const isSuccess = _.get(result, "data.testBroadcast");
        return isSuccess;
      })
      .catch((err) => {
        throw err;
      });
  },

  /**
   * Calculates estimated reach for a broadcast based on its targeting conditions.
   *
   * @param {ActionContext} context - Vuex action context
   * @param {Object} payload - Action payload
   * @param {IBroadcast} payload.broadcast - Broadcast object to estimate reach for
   * @returns {Promise<number>} Estimated reach count
   */
  GET_ESTIMATED_REACH: (store, { broadcast }) => {
    return graph
      .mutate({
        mutation: gql`
          mutation($broadcast: JSON) {
            getBroadcastEstimatedReach(broadcast: $broadcast)
          }
        `,
        variables: {
          broadcast,
        },
      })
      .then((result) => {
        const estimatedReach = _.get(result, "data.getBroadcastEstimatedReach");
        return estimatedReach;
      })
      .catch((err) => {
        throw err;
      });
  },
};

export default actions;
